/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import ContainerFluid from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService,
} from "../../../resources/validations/validations";

/* Components */
import UCButton from "../../buttons";
import UCToggle from "../../uctoggle";
/* Styles */
import styles from "./styles.js";

const messages = defineMessages({
  fieldNamePlaceholderMessage: {
    id: "settings.bookingFields.add.fieldNamePlaceholder",
  },
  fieldNameMatchesErrorMessage: {
    id: "addBookingFieldValidation.fieldMatches",
  },
});

const Content = ({ setShowModal, editItem, handleSubmit }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const formRef = useRef(null);
  const intl = useIntl();

  const { addBookingFieldMockedValidationObj } = useValidationService();
  const [bookingFieldValidationSchema, setBookingFieldValidationSchema] = useState({});
  const [maxError, setMaxError] = useState(false)
  const nameMaxLength = 20;

  useEffect(() => {
    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(
      addBookingFieldMockedValidationObj
    );
    setBookingFieldValidationSchema(result);
  }, []);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const checkInput = (minLength, maxLength) => {
    const min = Number(minLength);
    const max = Number(maxLength);
    if (min && max) {
      if (min > max) {
        setMaxError(true);
      } else {
        setMaxError(false);
      }
    } else {
      setMaxError(false);
    }
  };

  const submitForm = (values, setSubmitting) => {
    if (values.fieldName.length <= nameMaxLength) {
      const data = editItem
        ? { ...values, bookingFieldId: editItem?.bookingFieldId }
        : values;

      handleSubmit(data);
    }
    setSubmitting(false);
  };

  // regex used to show error message if field name starts with special character
  const fieldNameRegex = /^[!@#$%^&*()_+{}\[\]:;"'<>,.?\\|`~]/;

  return (
    <div className={classes.modalContent}>
      <Formik
        innerRef={formRef}
        initialValues={{
          fieldName: editItem?.fieldName || "",
          minLength: editItem?.minLength || "",
          maxLength: editItem?.maxLength || "",
          required: editItem?.required || false,
        }}
        validationSchema={bookingFieldValidationSchema}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          submitForm(values, setSubmitting, setErrors);
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <>
            <form onSubmit={handleSubmit} >
              <div className={classes.inputContainer}>
                <label htmlFor="fieldName" className={classes.label}>
                  <FormattedMessage id="settings.bookingFields.add.fieldName" />
                </label>
                <Field
                  placeholder={intl.formatMessage(
                    messages.fieldNamePlaceholderMessage
                  )}
                  type="text"
                  name="fieldName"
                  id="fieldName"
                  className={
                    errors.fieldName && touched.fieldName
                      ? "input-error"
                      : values.fieldName.length > nameMaxLength
                      ? "input-error"
                      : null
                  }
                />
                {errors.fieldName && touched.fieldName && (
                  <span className={classes.error}>
                    {errors.fieldName ==
                      intl.formatMessage(
                        messages.fieldNameMatchesErrorMessage
                      ) && fieldNameRegex.test(values.fieldName) ? (
                      <FormattedMessage id="settings.bookingFields.add.fieldNameError" />
                    ) : (
                      errors.fieldName
                    )}
                  </span>
                )}
                <span
                  className={
                    values.fieldName.length <= nameMaxLength
                      ? classes.lengthCount
                      : classes.error
                  }
                >
                  {`${values.fieldName.length}/${nameMaxLength}`}
                </span>
              </div>
              <div className={classes.inputContainer}>
                <label htmlFor="minLength" className={classes.label}>
                  <FormattedMessage id="settings.bookingFields.add.minLength" />
                </label>
                <Field
                  placeholder="1-30"
                  type="text"
                  name="minLength"
                  id="minLength"
                  onChange={(e) => {
                    handleChange(e);
                    checkInput(e.target.value, values.maxLength);
                  }}
                  className={
                    errors.minLength && touched.minLength
                      ? "input-error"
                      : !errors.minLength &&
                        values.maxLength &&
                        maxError
                      ? "input-error"
                      : null
                  }
                />
                <ErrorMessage
                  name="minLength"
                  component="span"
                  className={classes.error}
                />
              </div>
              <div className={classes.inputContainer}>
                <label htmlFor="maxLength" className={classes.label}>
                  <FormattedMessage id="settings.bookingFields.add.maxLength" />
                </label>
                <Field
                  placeholder="1-30"
                  type="text"
                  name="maxLength"
                  id="maxLength"
                  onChange={(e) => {
                    handleChange(e);
                    checkInput(values.minLength, e.target.value );
                  }}
                  className={
                    errors.maxLength && touched.maxLength
                      ? "input-error"
                      : !errors.maxLength && maxError
                      ? "input-error"
                      : null
                  }
                />
                <ErrorMessage
                  name="maxLength"
                  component="span"
                  className={classes.error}
                />
                {!errors.maxLength && maxError && (
                  <span className={classes.error}>
                    <FormattedMessage
                      id="settings.bookingFields.add.lengthError"
                      values={{
                        minLength: values.minLength,
                      }}
                    />
                  </span>
                )}
              </div>
              <hr className={classes.divider} />
              <div className={classes.toggleContainer}>
                <div>
                  <h6>
                    <FormattedMessage id="settings.bookingFields.add.requiredTitle" />
                  </h6>
                  <p>
                    <FormattedMessage id="settings.bookingFields.add.requiredText" />
                  </p>
                </div>
                <UCToggle
                  onChange={(e) => {
                    setFieldValue("required", e.target.checked);
                  }}
                  defaultChecked={values.required}
                  icons={null}
                />
              </div>
              <div className={classes.btnContainer}>
                <ContainerFluid>
                  <Row className={classes.btnsRow}>
                    <Col className={classes.colCancel}>
                      <UCButton
                        onClick={() => setShowModal(false)}
                        type="default"
                        className={classes.cancelBtn}
                        text={
                          <FormattedMessage id="settings.bookingFields.add.cancel" />
                        }
                      />
                    </Col>
                    <Col className={classes.colRemove}>
                      <UCButton
                        className={classes.createBtn}
                        onClick={handleFormSubmit}
                        type="primary"
                        text={
                          editItem ? (
                            <FormattedMessage id="settings.bookingFields.edit.button" />
                          ) : (
                            <FormattedMessage id="settings.bookingFields.add.button" />
                          )
                        }
                        disabled={
                          !values.fieldName ||
                          !values.minLength ||
                          !values.maxLength ||
                          Object.values(errors).length > 0 ||
                          maxError
                        }
                        spinner={{
                          name: "addEditBookingFieldSpinner",
                          size: 20,
                        }}
                      />
                    </Col>
                  </Row>
                </ContainerFluid>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default Content;
